import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiMapPin, FiArrowRight, FiStar } = FiIcons;

const StatePage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const states = [
    { name: 'Alabama', code: 'AL', slug: 'alabama', hasDetailedData: true },
    { name: 'Alaska', code: 'AK', slug: 'alaska', hasDetailedData: true },
    { name: 'Arizona', code: 'AZ', slug: 'arizona', hasDetailedData: true },
    { name: 'Arkansas', code: 'AR', slug: 'arkansas', hasDetailedData: true },
    { name: 'California', code: 'CA', slug: 'california', hasDetailedData: true },
    { name: 'Colorado', code: 'CO', slug: 'colorado', hasDetailedData: true },
    { name: 'Connecticut', code: 'CT', slug: 'connecticut', hasDetailedData: true },
    { name: 'Delaware', code: 'DE', slug: 'delaware', hasDetailedData: true },
    { name: 'Florida', code: 'FL', slug: 'florida', hasDetailedData: true },
    { name: 'Georgia', code: 'GA', slug: 'georgia', hasDetailedData: true },
    { name: 'Hawaii', code: 'HI', slug: 'hawaii', hasDetailedData: true },
    { name: 'Idaho', code: 'ID', slug: 'idaho', hasDetailedData: true },
    { name: 'Illinois', code: 'IL', slug: 'illinois', hasDetailedData: true },
    { name: 'Indiana', code: 'IN', slug: 'indiana', hasDetailedData: true },
    { name: 'Iowa', code: 'IA', slug: 'iowa', hasDetailedData: true },
    { name: 'Kansas', code: 'KS', slug: 'kansas', hasDetailedData: true },
    { name: 'Kentucky', code: 'KY', slug: 'kentucky', hasDetailedData: true },
    { name: 'Louisiana', code: 'LA', slug: 'louisiana', hasDetailedData: true },
    { name: 'Maine', code: 'ME', slug: 'maine', hasDetailedData: true },
    { name: 'Maryland', code: 'MD', slug: 'maryland', hasDetailedData: true },
    { name: 'Massachusetts', code: 'MA', slug: 'massachusetts', hasDetailedData: true },
    { name: 'Michigan', code: 'MI', slug: 'michigan', hasDetailedData: true },
    { name: 'Minnesota', code: 'MN', slug: 'minnesota', hasDetailedData: true },
    { name: 'Mississippi', code: 'MS', slug: 'mississippi', hasDetailedData: true },
    { name: 'Missouri', code: 'MO', slug: 'missouri', hasDetailedData: true },
    { name: 'Montana', code: 'MT', slug: 'montana', hasDetailedData: true },
    { name: 'Nebraska', code: 'NE', slug: 'nebraska', hasDetailedData: true },
    { name: 'Nevada', code: 'NV', slug: 'nevada', hasDetailedData: true },
    { name: 'New Hampshire', code: 'NH', slug: 'new-hampshire', hasDetailedData: true },
    { name: 'New Jersey', code: 'NJ', slug: 'new-jersey', hasDetailedData: true },
    { name: 'New Mexico', code: 'NM', slug: 'new-mexico', hasDetailedData: true },
    { name: 'New York', code: 'NY', slug: 'new-york', hasDetailedData: true },
    { name: 'North Carolina', code: 'NC', slug: 'north-carolina', hasDetailedData: true },
    { name: 'North Dakota', code: 'ND', slug: 'north-dakota', hasDetailedData: true },
    { name: 'Ohio', code: 'OH', slug: 'ohio', hasDetailedData: true },
    { name: 'Oklahoma', code: 'OK', slug: 'oklahoma', hasDetailedData: true },
    { name: 'Oregon', code: 'OR', slug: 'oregon', hasDetailedData: true },
    { name: 'Pennsylvania', code: 'PA', slug: 'pennsylvania', hasDetailedData: true },
    { name: 'Rhode Island', code: 'RI', slug: 'rhode-island', hasDetailedData: true },
    { name: 'South Carolina', code: 'SC', slug: 'south-carolina', hasDetailedData: true },
    { name: 'South Dakota', code: 'SD', slug: 'south-dakota', hasDetailedData: true },
    { name: 'Tennessee', code: 'TN', slug: 'tennessee', hasDetailedData: true },
    { name: 'Texas', code: 'TX', slug: 'texas', hasDetailedData: true },
    { name: 'Utah', code: 'UT', slug: 'utah', hasDetailedData: true },
    { name: 'Vermont', code: 'VT', slug: 'vermont', hasDetailedData: true },
    { name: 'Virginia', code: 'VA', slug: 'virginia', hasDetailedData: true },
    { name: 'Washington', code: 'WA', slug: 'washington', hasDetailedData: true },
    { name: 'Washington DC', code: 'DC', slug: 'washington-dc', hasDetailedData: true },
    { name: 'West Virginia', code: 'WV', slug: 'west-virginia', hasDetailedData: true },
    { name: 'Wisconsin', code: 'WI', slug: 'wisconsin', hasDetailedData: true },
    { name: 'Wyoming', code: 'WY', slug: 'wyoming', hasDetailedData: true }
  ];

  // Filter states based on search term - include all states that match
  const filteredStates = states.filter(state => 
    state.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to get flag URL from a reliable flag service
  const getFlagUrl = (stateCode) => {
    if (stateCode === 'DC') {
      return `https://flagsapi.com/US-DC/flat/64.png`;
    }
    return `https://flagsapi.com/US-${stateCode}/flat/64.png`;
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-secondary-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            State-Specific Family Law Resources
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Select your state to access comprehensive family law information, including custody laws, child support guidelines, and local court procedures tailored to fathers' rights.
          </motion.p>
        </div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative max-w-md mx-auto mb-12"
        >
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SafeIcon icon={FiSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search for a state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
          />
        </motion.div>

        {/* States Display */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: searchTerm ? 0.2 : 0.3 }}
        >
          {searchTerm && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Search Results for "{searchTerm}"
              </h2>
            </div>
          )}

          {!searchTerm && (
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Comprehensive Family Law Resources
              </h2>
              <p className="text-gray-600">
                Detailed guides, forms, and resources available for all 50 states and Washington DC
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(searchTerm ? filteredStates : states).map((state, index) => (
              <motion.div
                key={state.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <Link
                  to={`/states/${state.slug}`}
                  className={`group block rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 p-6 relative overflow-hidden ${
                    state.hasDetailedData
                      ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-white'
                      : 'bg-white border border-primary-100 hover:border-primary-300'
                  }`}
                >
                  {state.hasDetailedData && (
                    <div className="absolute top-2 right-2">
                      <div className="bg-white/20 rounded-full p-1">
                        <SafeIcon icon={FiStar} className="text-white text-sm" />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-4 mb-4">
                    <div
                      className={`w-16 h-16 rounded-lg flex items-center justify-center p-2 flex-shrink-0 ${
                        state.hasDetailedData ? 'bg-white/20' : 'bg-primary-50'
                      }`}
                    >
                      <img
                        src={getFlagUrl(state.code)}
                        alt={`${state.name} flag`}
                        className="w-full h-full object-cover rounded-md shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-full items-center justify-center">
                        <SafeIcon
                          icon={FiMapPin}
                          className={`text-2xl ${
                            state.hasDetailedData ? 'text-white' : 'text-primary-600'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3
                        className={`text-lg font-semibold ${
                          state.hasDetailedData
                            ? 'text-white group-hover:text-primary-100'
                            : 'text-gray-900 group-hover:text-primary-600'
                        }`}
                      >
                        {state.name}
                      </h3>
                      <p
                        className={`text-sm ${
                          state.hasDetailedData ? 'text-primary-100' : 'text-gray-500'
                        }`}
                      >
                        {state.code}
                      </p>
                      <p
                        className={`text-xs mt-1 font-medium ${
                          state.hasDetailedData ? 'text-primary-200' : 'text-yellow-600'
                        }`}
                      >
                        {state.hasDetailedData ? 'Complete Guide Available' : 'Basic Info Only'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${
                        state.hasDetailedData ? 'text-primary-100' : 'text-gray-600'
                      }`}
                    >
                      View Resources
                    </span>
                    <SafeIcon
                      icon={FiArrowRight}
                      className={`group-hover:translate-x-1 transition-transform ${
                        state.hasDetailedData
                          ? 'text-primary-100'
                          : 'text-gray-400 group-hover:text-primary-600'
                      }`}
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredStates.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No states found matching "{searchTerm}"</p>
            </div>
          )}
        </motion.div>

        {/* Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 bg-white rounded-lg border border-primary-200 p-8"
        >
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Understanding Our Resources
            </h3>
            <p className="text-gray-600">
              We now provide comprehensive coverage for all states
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-primary-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiStar} className="text-primary-600 mr-2" />
                <h4 className="font-semibold text-primary-900">Comprehensive Resources</h4>
              </div>
              <p className="text-primary-800 text-sm">
                All states now have complete guides with detailed custody laws, child support guidelines, court procedures, filing requirements, and father-specific guidance.
              </p>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiMapPin} className="text-yellow-600 mr-2" />
                <h4 className="font-semibold text-yellow-900">Regular Updates</h4>
              </div>
              <p className="text-yellow-800 text-sm">
                We regularly update our state information to reflect the latest legal changes and resources. Our data is sourced from official state websites, legal resources, and family law experts.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StatePage;